// Installable app (PWA) support: service worker registration and the install prompt.
// Imported once from src/index.js so `beforeinstallprompt` is captured even if it fires
// before any component that shows the install button has mounted.
// Only the chat app (src/services/chatApp.js) is installable, so the prompt is always for IRG Chat.

const INSTALLED_KEY = "irg-chat-app-installed";
const readInstalledFlag = () => {
  try {
    return localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
};
const writeInstalledFlag = (on) => {
  try {
    if (on) localStorage.setItem(INSTALLED_KEY, "1");
    else localStorage.removeItem(INSTALLED_KEY);
  } catch {
    /* prompts just show again */
  }
};

let deferredPrompt = null;
// Installed from this browser before. The browser can't tell an ERP tab whether the chat app is installed.
let installed = readInstalledFlag();
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

export const isRunningStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

// iPhone/iPad (iPadOS reports itself as a Mac, but has touch). No install prompt there, and web push
// only works once the app is added to the Home Screen.
export const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
  (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);

// iOS/iPadOS Safari and desktop Safari/Firefox have no install prompt API; users install via the browser menu.
export const getManualInstallHint = () => {
  const ua = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'Tap the Share button, then "Add to Home Screen".';
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua)) {
    return 'In Safari, choose File → "Add to Dock".';
  }
  if (/Firefox/.test(ua)) return "Firefox can't install apps. Open this site in Chrome or Edge to install it.";
  return null;
};

export const getInstallState = () => ({
  canInstall: !!deferredPrompt,
  installed: installed || isRunningStandalone(),
});

export const subscribeInstallState = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const promptInstall = async () => {
  if (!deferredPrompt) return "unavailable";
  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  promptEvent.prompt();
  const { outcome } = await promptEvent.userChoice;
  notify();
  return outcome;
};

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredPrompt = event;
  // The browser only offers this when the app isn't installed (it may have been uninstalled since).
  installed = false;
  writeInstalledFlag(false);
  notify();
});

window.addEventListener("appinstalled", () => {
  installed = true;
  writeInstalledFlag(true);
  deferredPrompt = null;
  notify();
});

// ---------- Web Push (chat notifications while the app is closed) ----------

export const pushSupported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const urlBase64ToUint8Array = (base64) => {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
};

// The service worker only exists in production builds (and the installed app).
const getRegistration = async () => (pushSupported() ? navigator.serviceWorker.getRegistration() : null);

export const getPushSubscription = async () => {
  const registration = await getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
};

// Resolves to the subscription JSON to send to the server. Throws Error("no-service-worker") or
// Error("permission-denied").
export const enablePush = async (publicKey) => {
  const registration = await getRegistration();
  if (!registration) throw new Error("no-service-worker");
  if ((await Notification.requestPermission()) !== "granted") throw new Error("permission-denied");
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing || (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  return subscription.toJSON();
};

// Unsubscribes this browser; resolves to the old endpoint (to remove it on the server) or null.
export const disablePush = async () => {
  const subscription = await getPushSubscription();
  if (!subscription) return null;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
};

// Only register in production builds so the dev server never serves stale cached bundles.
export const registerServiceWorker = () => {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL}/service-worker.js`)
      .catch((err) => console.error("Service worker registration failed:", err));
  });
};
